import { Component, Vue, Watch } from 'vue-property-decorator';
import { AxiosError, AxiosResponse } from 'axios';
import { cloneDeep } from 'lodash';
import LoaderCard from '@/components/loader-card/LoaderCard';
import debounce from 'debounce';

import ErrorHandler from '@/support/ErrorHandler';
import { Brand } from '@/models/Brand';
import { Color } from '@/models/Color';
import MiButton from '@/components/mi-button/MiButton';
import { Options } from '@/components/mi-dialog/MiDialog';

import { Attribute } from '@/models/Attribute';
import CreateAttributeDialog from '@/views/Settings/Attributes/CreateDialog';

import { Category } from '@/models/Category';
import CreateCategoryDialog from '@/views/Settings/Categories/CreateDialog';
import CreateSubCategoryDialog from '@/views/Settings/Categories/CreateSubCategoryDialog';

import { Tag } from '@/models/Tag';
import CreateTagDialog from '@/views/Settings/Tags/CreateDialog';
import { AttributeOption } from '@/models/AttributeOption';
import { AttributeValue } from '@/models/AttributeValue';
import { Size } from '@/models/Size';
import { Supplier } from '@/models/Supplier';

import UploadProductImageDialog from './UploadProductImageDialog';
import DataTable, { TableVisibility } from '@/components/data-table/DataTable';
import { Season } from '@/models/Season';

import RenderFlow from '@/support/RenderFlow';
import { Material } from '@/models/Material';

import { ProductVariant, ProductVariantIncludes } from '@/models/ProductVariant';
import { Product, ProductIncludes } from '@/models/Product';
import { ActionItem } from '@/components/ActionList/ActionList';
import UploadProductCsvDialog from '@/views/Variants/UploadProductCsvDialog';
import { axiosDownload } from '@/support/Client';
import CopyToVariantDialog from '@/components/CopyToVariantDialog/CopyToVariantDialog';
import { SupplierSize } from '@/models/SupplierSize';

@Component<Variant>({
  components: {
    LoaderCard,
    MiButton,
    CreateAttributeDialog,
    CreateCategoryDialog,
    CreateSubCategoryDialog,
    CreateTagDialog,
    UploadProductImageDialog,
    UploadProductCsvDialog,
    CopyToVariantDialog
  },
})
export default class Variant extends Vue {
  public $pageTitle = 'Variant';
  protected productVariant: ProductVariant = new ProductVariant();
  protected productVariantClone: ProductVariant | null = null;
  protected product: Product | null = null;

  /**
   * isEditingProduct is set true by click.
   * render flow control.
   * turns texts into inputs.
   * watcher on isEditingProducts creates clone of product.
   *
   * @protected
   * @type {boolean}
   * @memberof Product
   */
  protected isEditingProduct: boolean = false;

  /**
   * isAddingCategory is set true by click.
   * render flow control.
   * shows form for adding categories.
   * set false by update request callback
   *
   * @protected
   * @type {boolean}
   * @memberof Product
   */
  protected isAddingCategory: boolean = false;

  /**
   * isUploadingImages set true by click.
   * render flow control.
   * shows form for uploading images and associating with product / ~ variants
   * set false by dialog exit or completion
   *
   * @protected
   * @type {boolean}
   * @memberof Product
   */
  protected isUploadingImages: boolean = false;

  private RenderFlow = new RenderFlow([
    'productIsLoaded',
    'isUpdating',
    'isSaved',
    'isEditingProduct',
    'updateSuccess'
  ]);

  /**
   * used by ownership select
   */
  public ownershipOptions: object[] = [
    { id: 'maison', name: 'Maison' },
    { id: 'supplier', name: 'Supplier' }
  ];

  protected selectedNewAttribute: Attribute | null = null;
  protected selectedAttribute: Attribute | null = null;
  protected selectedAttributeOption: AttributeOption | null = null;
  protected attribute: Attribute | null = null;
  protected isAddingAttribute: boolean = false;
  protected isDisplayingQuickAddProductsDialog: boolean = false;
  protected isDisplayingCopyToVariantDialog: boolean = false;

  protected selectedCategory: Category | null = null;
  protected selectedCategories: Category[] | null = null;
  protected categories: Category[] | null = null;
  protected isCreatingCategory: boolean = false;

  protected tags: Tag[] | null = null;
  protected materials: Material[] | null = null;
  protected selectedTags: Tag[] | null = null;
  protected selectedTag: Tag | null = null;
  protected selectedMaterials: Material[] | null = null;
  protected selectedMaterial: Material | null = null;
  protected selectedMaterialPercentage = 0;
  protected isAddingTags: boolean = false;
  protected isAddingMaterials: boolean = false;
  protected isUploadingCsv: boolean = false;
  protected isExportingProductVariants: boolean = false;

  protected minimumDateForDatePicker: string = '';
  protected maximumDateForDatePicker: string = '';

  protected selectedImage: any | null = null;

  protected activeTab: string = 'default';
  protected nullableDate: string = '0000-00-00 00:00:00';

  protected selectedStatus: string = '';
  /**
   * statuses are product statuses.
   * concept, active, inactive.
   * array of objects with 'text' and 'value' keys.
   * text is friendly form, value is safe form.
   *
   * @protected
   * @type {Status[]}
   * @memberof Product
   */
  protected statuses: Status[] = [
    {
      text: 'Concept',
      value: 'concept'
    },
    {
      text: 'Active',
      value: 'active'
    },
    {
      text: 'Inactive',
      value: 'inactive'
    }
  ];

  protected sizes: Size[] = [];
  protected upc: string = '';
  protected suppliers: Supplier[] = [];
  protected seasons: Season[] = [];
  protected brands: Brand[] = [];
  protected seasonSearch: string = '';
  protected supplierSearch: string = '';
  protected sizeSearch: string = '';
  protected colorSearch: string = '';
  protected brandSearch: string = '';

  /**
   * set by product variant delete, temp ref to product variant.
   * product if deleting, null if not deleting
   *
   * @protected
   * @type {(ProductVariant | null)}
   * @memberof Product
   */
  protected productVariantModelToDelete: ProductVariant | null = null;
  /**
   * After deleting product variant datatable must update with new
   * API call. This holds the reference to the datatable while deleting.
   * null when not deleting product variant.
   *
   * @protected
   * @type {(DataTable | null)}
   * @memberof Product
   */
  protected dataTableToRefresh: DataTable | null = null;

  protected isShowingBlockDatePicker: boolean = false;
  protected isShowingFinalReturnDatePicker: boolean = false;

  public searchBrandDebounce: Function = this.handleBrandSearch();
  public searchSizeDebounce: Function = this.handleSizeSearch();
  public searchColorDebounce: Function = this.handleColorSearch();
  public searchSupplierDebounce: Function = this.handleSupplierSearch();

  protected isCopyingAttributesToAVariant = false;

  protected colors: Color[] = [];

  protected visibility: TableVisibility = {
    checkboxes: false,
    limit: false,
    search: false,
    title: false,
    total: false,
    pagination: false
  };

  public mounted() {
    this.initialize();
  }

  protected async initialize() {
    this.RenderFlow.set('isUpdating', false);
    this.RenderFlow.set('showDeniedProductDialog', false);
    this.RenderFlow.set('isLoading', true);

    await this.getProductVariant();
    await this.getProduct();

    this.RenderFlow.set('productIsLoaded', true);

    // TODO: split into functions
    this.initBreadcrumb();

    if (this.productVariant.size) {
      this.sizes.push(this.productVariant.size as Size);
    }
    if (this.productVariant.brand) {
      this.brands.push(this.productVariant.brand as Brand);
    }
    if (this.productVariant.color) {
      this.colors.push(this.productVariant.color as Color);
    }
    if (this.productVariant.supplier) {
      this.suppliers.push(this.productVariant.supplier as Supplier);
    }
    this.selectedStatus = this.productVariant.status ? this.productVariant.status : '';

    this.setAttributeOptions();
    this.getCategories();
    this.getTags();
    this.getMaterials();

    this.RenderFlow.set('isLoading', false);
  }

  protected onImagesUploaded(): void {
    setTimeout(() => {
      this.initialize();
    }, 1000);
  }

  protected async getProductVariant(): Promise<void> {
    try {
      this.productVariant = await new ProductVariant()
        .include([
          ProductVariantIncludes.ATTRIBUTES,
          ProductVariantIncludes.IMAGES,
          ProductVariantIncludes.MATERIALS,
          ProductVariantIncludes.SEASON,
          ProductVariantIncludes.TAGS,
          ProductVariantIncludes.AVAILABLE_SIZES,
          ProductVariantIncludes.CATEGORIES,
          ProductVariantIncludes.ATTRIBUTES_COUNT,
          ProductVariantIncludes.COLOR,
          ProductVariantIncludes.BRAND,
          ProductVariantIncludes.SUPPLIER,
          ProductVariantIncludes.SIZE,
          ProductVariantIncludes.SUPPLIER_SIZE_OBJECT,
        ])
        .find(this.$route.params.id)

    } catch (error) {
      ErrorHandler.network(error);
    }
  }

  protected async getProduct(): Promise<void> {
    try {
      this.product = await new Product()
        .include([
          ProductIncludes.BRAND,
          ProductIncludes.CATEGORIES,
          ProductIncludes.SUPPLIER,
          ProductIncludes.METADATA
        ])
        .find(this.productVariant.product_uuid)

    } catch (error) {
      ErrorHandler.network(error);
    }
  }

  protected get attributeSummary() {
    if (!this.productVariant || !this.productVariant.attributes) {
      return;
    }

    const grouped = this.productVariant.attributes.filter((attribute, index, self) =>
      index === self.findIndex((indexed) => (
        indexed.title === attribute.title
      )));

      const test: attributeItem[] = grouped.map((attribute: Attribute) => {
        return {
          title: attribute.title,
          values: this.getSelectedOptions(`${attribute.title}`),
        } as attributeItem;
      });

      return test;
  }

  protected getSelectedOptions(title: string) {
    if (!this.productVariant || !this.productVariant.attributes) {
      return;
    }

    const attributes = this.productVariant.attributes.filter((indexedAttribute: Attribute) => {
      return indexedAttribute.title === title;
    });

    const result: attributeValue[] = attributes.map((attribute: Attribute) => {
      return {
        attribute: attribute,
        option: attribute.value,
      } as attributeValue;
    });

    return result;
  }

  protected setAttributeOptions() {
    if (!this.productVariant || !this.productVariant.attributes) {
      return;
    }

    this.productVariant.attributes.forEach((attribute: Attribute) => {
      if (attribute.options && attribute.options.length) {
        const value = attribute.options.find((option: AttributeOption) => {
          return option.id === attribute.value;
        });

        (attribute.value as any) = value ? value : attribute.value;
      }
    });
  }

  protected getSuppliers(filter: string = '') {
    new Supplier()
      .filter('search', filter)
      .limit(100)
      .all()
      .then((suppliers: Supplier[]) => {
        this.suppliers = suppliers;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected getBrands(filter: string = '') {
    new Brand()
      .filter('search', filter)
      .limit(100)
      .all()
      .then((brands: Brand[]) => {
        this.brands = brands;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected getSizes(filter: string = '') {
    new Size()
      .filter('search', filter)
      .limit(100)
      .all()
      .then((sizes: Size[]) => {
        this.sizes = sizes;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected getColors(filter: string = '') {
    new Color()
      .filter('search', filter)
      .limit(999) // so we can also search inside all the sub-items
      .all()
      .then((colors: Color[]) => {
        const parsedColors: Color[] = [];

        colors.forEach((color: Color) => {
          parsedColors.push(color);
          if (color.sub_colors) {
            color.sub_colors.forEach((subColor: any) => {
              parsedColors.push(subColor);
            });
          }
        });

        this.colors = parsedColors;
      })
      .catch((error: AxiosError) => {});
  }

  protected getCategories() {
    new Category()
      .limit(100)
      .all()
      .then((categories: Category[]) => {
        let subCategories: any = [];
        categories.forEach((categorie: Category) => {
          if (categorie.sub_categories) {
            subCategories = [...subCategories, ...categorie.sub_categories];
          }
        });

        this.categories = [...categories, ...subCategories];

        this.categories = this.categories.filter((category: Category) => {
          if (this.productVariant.categories) {
            return !this.productVariant.categories.some(
              (productCategory: Category) => {
                return category.id === productCategory.id;
              }
            );
          }
        });
      })
      .catch((error: AxiosError) => {});
  }

  protected getTags() {
    new Tag()
      .limit(100)
      .all()
      .then((tags: Tag[]) => {
        this.tags = tags.filter((tag: Tag) => {
          if (this.productVariant.tags) {
            return !this.productVariant.tags.some((productTag: Tag) => {
              return tag.id === productTag.uuid;
            });
          }
        });
      })
      .catch((error: AxiosError) => {});
  }

  protected getMaterials() {
    new Material()
      .limit(100)
      .all()
      .then((materials: Material[]) => {
        this.materials = materials.filter((material: Material) => {
          if (this.productVariant.materials) {
            return !this.productVariant.materials.some((productMaterial: Material) => {
              return material.id === productMaterial.uuid;
            });
          }
        });
      })
      .catch((error: AxiosError) => {});
  }

  protected async exportProductVariants(): Promise<void> {
    if (!this.product) {
      return;
    }

    this.isExportingProductVariants = true;
    try {
      await axiosDownload({
        url: new Product().exportProduct(this.product.id),
        accept: 'text/csv; charset=utf-8'
      });
    } catch (error) {
      ErrorHandler.network(error);
    }
    this.isExportingProductVariants = false;
  }

  protected handleSupplierSearch() {
    return debounce((searchInput: string) => {
      this.getSuppliers(searchInput);
    }, 400);
  }

  protected handleSizeSearch() {
    return debounce((searchInput: string) => {
      this.getSizes(searchInput);
    }, 400);
  }

  protected handleColorSearch() {
    return debounce((searchInput: string) => {
      this.getColors(searchInput);
    }, 400);
  }

  protected handleBrandSearch() {
    return debounce((searchInput: string) => {
      this.getBrands(searchInput);
    }, 400);
  }

  protected initBreadcrumb() {
    this.$root.$emit('breadcrumbUpdated', {
      crumb: [
        { name: 'Producten', path: '/producten' },
        { name: this.productVariant.product_name || '', path: this.$route.path }
      ]
    });
  }

  protected resetProduct() {
    if (!this.productVariantClone) {
      return;
    }
    this.productVariant = cloneDeep(this.productVariantClone);
    this.productVariantClone = null;

    this.sizes.push(this.productVariant.size as Size);
    this.brands.push(this.productVariant.brand as Brand);
    this.colors.push(this.productVariant.color as Color);

    this.setAttributeOptions();
  }

  protected update() {
    this.RenderFlow.set('isUpdating', true);

    this.productVariant
      .update(this.variantPayload)
      .then(() => {
        this.RenderFlow.set('updateSuccess', true);
        this.RenderFlow.set('showDeniedProductDialog', false);
        this.RenderFlow.set('isEditingProduct', false);
        this.initialize();
      })
      .catch((error: AxiosError) => {
        this.RenderFlow.set('isUpdating', false);
        ErrorHandler.network(error);
      });
  }

  protected get variantPayload() {
    const payload = cloneDeep(this.productVariant);

    payload.brand = payload.brand && (payload.brand as Brand).id ? (payload.brand as Brand).id : null;
    payload.color = payload.color && (payload.color as Color).id ? (payload.color as Color).id : null;
    payload.size = payload.size && (payload.size as Size).id ? (payload.size as Size).id : null;
    payload.supplier_size_object = payload.supplier_size_object && (payload.supplier_size_object as SupplierSize).id ? (payload.supplier_size_object as SupplierSize).id : null;
    payload.supplier_size = payload.supplier_size_object;
    payload.season = payload.season && (payload.season as Season).id ? (payload.season as Season).id : null;
    payload.supplier = payload.supplier && (payload.supplier as Supplier).id ? (payload.supplier as Supplier).id : '';
    if (payload.tags) {
      // @ts-ignore
      payload.tags = payload.tags.map(tag => tag.uuid);
    }
    payload.status = this.selectedStatus;

    return payload;
  }

  protected addCategories() {
    if (!this.selectedCategories) {
      return;
    }

    this.selectedCategories.forEach((category: Category) => {
      if (!this.productVariant || !this.productVariant.product_uuid || !category.id) {
        return;
      }

      new ProductVariant()
        .attachCategory(this.productVariant.product_uuid, category.id)
        .then((response: AxiosResponse) => {
          this.initialize();
          this.closeAddCategory();
        })
        .catch((error: AxiosError) => {
          ErrorHandler.network(error);
        });
    });
  }

  protected closeAddCategory() {
    this.selectedCategories = null;
    this.isAddingCategory = false;
  }

  protected deleteCategory(category: Category) {
    this.selectedCategory = new Category(category);
    this.$store.dispatch('openDialog', this.dialogOptionsDeleteCategory);
  }

  protected syncStock() {
    this.$store.dispatch('openDialog', this.dialogOptionsSyncStock);
  }

  get dialogOptionsSyncStock(): Options {
    return {
      title: 'Stock',
      text: 'Stock handmatig ophalen uit Peoplevox?',
      type: 'warning',
      buttons: {
        confirm: {
          text: 'Ok',
          action: () => {
            new ProductVariant()
              .syncStock(this.$route.params.id)
              .then((response: any) => {
                if (response) {
                  this.initialize();
                }
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          }
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString()
        }
      }
    };
  }

  get dialogOptionsDeleteCategory(): Options {
    return {
      title:
        (this.selectedCategory as any).title ||
        this.$t('dialogOptions.confirmation').toString(),
      text: this.$tc('dialogOptions.delete', 2, {
        item: this.$tc('category.title', 1)
      })
        .toString()
        .toLowerCase(),
      type: 'warning',
      buttons: {
        confirm: {
          text: this.$t('dialogOptions.button.delete').toString(),
          action: () => {
            if (
              !this.productVariant ||
              !this.productVariant.product_uuid ||
              !this.selectedCategory ||
              !this.selectedCategory.id
            ) {
              return;
            }

            new ProductVariant()
              .removeAttachedCategory(
                this.productVariant.product_uuid,
                this.selectedCategory.id
              )
              .then((response: any) => {
                if (response) {
                  this.initialize();
                  this.selectedCategory = null;
                }
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          }
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
          action: () => {
            this.selectedCategory = null;
          }
        }
      }
    };
  }

  protected addTags() {
    if (!this.selectedTags) {
      return;
    }

    this.selectedTags.forEach((tag: Tag) => {
      if (!this.productVariant || !this.productVariant.id || !tag.id) {
        return;
      }

      new ProductVariant()
        .addProductTag(this.productVariant.id, tag.id)
        .then((response: AxiosResponse) => {
          this.initialize();
          this.closeAddTags();
        })
        .catch((error: AxiosError) => {
          ErrorHandler.network(error);
        });
    });
  }

  protected closeAddTags() {
    this.selectedTags = null;
    this.isAddingTags = false;
  }

  protected deleteTag(tag: Tag) {
    this.selectedTag = new Tag(tag);
    this.$store.dispatch('openDialog', this.dialogOptionsDeleteTag);
  }

  protected handleChangePercentage() {
    if (this.selectedMaterialPercentage > 100) {
      this.selectedMaterialPercentage = 100;
    }

    if (this.selectedMaterialPercentage < 0) {
      this.selectedMaterialPercentage = 0;
    }
  }

  protected async addMaterial () {
    if (!this.selectedMaterial || this.selectedMaterialPercentage === 0) {
      return;
    }

    const payload = {
      materials: [
        ...[{ id: this.selectedMaterial.id, percentage: this.selectedMaterialPercentage }]
      ],
    }

    if (this.productVariant.materials && this.productVariant.materials.length > 0) {
      payload.materials = [...payload.materials, ...this.productVariant.materials as any]
    }

    await new ProductVariant(this.productVariant)
        .update(payload)
        .then((response: AxiosResponse) => {
          this.initialize();
          this.closeAddMaterials();
        })
        .catch((error: AxiosError) => {
          ErrorHandler.network(error);
        });

  }

  protected closeAddMaterials() {
    this.selectedMaterials = null;
    this.selectedMaterial = null;
    this.selectedMaterialPercentage = 0;
    this.isAddingMaterials = false;
  }

  protected async deleteMaterial(material: Material) {
    this.selectedMaterial = material;
    this.$store.dispatch('openDialog', this.dialogOptionsDeleteMaterial);
  }

  get dialogOptionsDeleteTag(): Options {
    return {
      title:
        (this.selectedTag as any).name ||
        this.$t('dialogOptions.confirmation').toString(),
      text: this.$tc('dialogOptions.delete', 2, {
        item: this.$tc('tag.title', 1)
      })
        .toString()
        .toLowerCase(),
      type: 'warning',
      buttons: {
        confirm: {
          text: this.$t('dialogOptions.button.delete').toString(),
          action: () => {
            if (
              !this.productVariant ||
              !this.productVariant.id ||
              !this.selectedTag ||
              !this.selectedTag.uuid
            ) {
              return;
            }

            new ProductVariant()
              .removeProductTag(this.productVariant.id, this.selectedTag.uuid)
              .then((response: any) => {
                if (response) {
                  this.initialize();
                  this.selectedCategory = null;
                }
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          }
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
          action: () => {
            this.selectedCategory = null;
          }
        }
      }
    };
  }

  get dialogOptionsDeleteMaterial(): Options {
    return {
      title:
        `${(this.selectedMaterial as any).percentage}% ${(this.selectedMaterial as any).name}` ||
        this.$t('dialogOptions.confirmation').toString(),
      text: 'Weet je zeker dat je dit materiaal wilt verwijderen?',
      type: 'warning',
      buttons: {
        confirm: {
          text: this.$t('dialogOptions.button.delete').toString(),
          action: async () => {
            if (!this.productVariant.materials) {
              return;
            }

            const currentMaterials = this.productVariant.materials;
            const selected = this.selectedMaterial;

            if (!selected) {
              return;
            }

            const filtered = currentMaterials.filter(function(indexedMaterial) { return indexedMaterial.id !== selected.id || indexedMaterial.percentage !== selected.percentage; });

            const payload = {
              materials: filtered,
            }

            await new ProductVariant(this.productVariant)
                .update(payload)
                .then((response: AxiosResponse) => {
                  this.selectedMaterial = null;
                  this.initialize();
                })
                .catch((error: AxiosError) => {
                  ErrorHandler.network(error);
                });
          }
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
          action: () => {
            this.selectedMaterial = null;
          }
        }
      }
    };
  }

  protected setDefaultImage(image: any) {
    this.selectedImage = image;
    this.$store.dispatch('openDialog', this.dialogOptionsSetDefaultImage);
  }

  get dialogOptionsSetDefaultImage(): Options {
    return {
      title:
        (this.selectedImage as any).name ||
        this.$t('dialogOptions.confirmation').toString(),
      text: 'Weet je zeker dat je deze foto als default wilt hebben?',
      type: 'warning',
      buttons: {
        confirm: {
          text: this.$t('dialogOptions.button.ok').toString(),
          action: () => {
            if (
              !this.productVariant ||
              !this.productVariant.id ||
              !this.selectedImage ||
              !this.selectedImage.id
            ) {
              return;
            }

            const payload = {
              image: this.selectedImage.id,
              image_origin: this.selectedImage.origin
            };

            new ProductVariant()
              .setDefaultImage(this.productVariant.id, payload)
              .then((response: any) => {
                if (response) {
                  this.initialize();
                  this.selectedCategory = null;
                }
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          }
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
          action: () => {
            this.selectedCategory = null;
          }
        }
      }
    };
  }

  protected deleteImage(image: any) {
    this.selectedImage = image;
    this.$store.dispatch('openDialog', this.dialogOptionsDeleteImage);
  }

  get dialogOptionsDeleteImage(): Options {
    return {
      title:
        (this.selectedImage as any).name ||
        this.$t('dialogOptions.confirmation').toString(),
      text: this.$tc('dialogOptions.delete', 2, {
        item: this.$tc('image.title', 1)
      })
        .toString()
        .toLowerCase(),
      type: 'warning',
      buttons: {
        confirm: {
          text: this.$t('dialogOptions.button.delete').toString(),
          action: () => {
            if (
              !this.productVariant ||
              !this.productVariant.id ||
              !this.selectedImage ||
              !this.selectedImage.id
            ) {
              return;
            }

            const payload = {
              image: this.selectedImage.id,
              image_origin: this.selectedImage.origin
            };

            new ProductVariant()
              .detachImage(this.productVariant.id, payload)
              .then((response: any) => {
                if (response) {
                  this.initialize();
                  this.selectedCategory = null;
                }
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          }
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
          action: () => {
            this.selectedCategory = null;
          }
        }
      }
    };
  }

  protected updateAttribute(attribute: Attribute) {
    new AttributeValue(attribute)
      .update({
        value: attribute.value
      })
      .then((response: any) => {
        if (response) {
          this.initialize();
        }
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected deleteAttribute(attribute: Attribute) {
    this.selectedAttribute = attribute;
    this.$store.dispatch('openDialog', this.dialogOptionsDeleteAttribute);
  }

  /**
   * the action used by the datatable on click delete product variant.
   * sets references to datatable instance and product variant to delete
   * dispatches the prompt
   *
   * @protected
   * @param {DataTable} datatableInstance
   * @param {*} productVariantModelToDelete
   * @memberof Product
   */
  protected deleteProductVariantPrompt(
    datatableInstance: DataTable,
    productVariantModelToDelete: ProductVariant
  ) {
    this.dataTableToRefresh = datatableInstance;
    this.productVariantModelToDelete = productVariantModelToDelete as ProductVariant;
    this.$store.dispatch('openDialog', this.dialogOptionsDeleteProductVariant);
  }

  /**
   * sets the reference to the to-delete product variant to null;
   * refreshes the datatable with product variants;
   * sets reference to datatable to null
   *
   * @protected
   * @memberof Product
   */
  protected cleanupAfterDeleteProductVariantPrompt() {
    this.productVariantModelToDelete = null;
    if (this.dataTableToRefresh) {
      this.dataTableToRefresh.refresh();
      this.dataTableToRefresh = null;
    }
  }

  /**
   * generic dialog options to populate dialog with.
   * after complete prompt, positive or negative,
   * cleanupAfterDeleteProductVariantPrompt is called
   * productVariantModelToDelete is set by action on delete button, deleteProductVariantPrompt
   *
   * @readonly
   * @type {Options}
   * @memberof Product
   */
  get dialogOptionsDeleteProductVariant(): Options {
    const productVariantModelToDelete = this
      .productVariantModelToDelete as ProductVariant;
    return {
      title: `Bevestig verwijderen`,
      text: `Weet je zeker dat je variant <strong>${productVariantModelToDelete.sku}</strong> wilt verwijderen?`,
      type: 'warning',
      buttons: {
        confirm: {
          text: this.$t('dialogOptions.button.delete').toString(),
          action: () => {
            this.productVariant
              .deleteProduct(productVariantModelToDelete.id as string)
              .then((response: any) => {
                // de rien
                this.cleanupAfterDeleteProductVariantPrompt();
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
                this.cleanupAfterDeleteProductVariantPrompt();
              });
          }
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
          action: () => {
            this.cleanupAfterDeleteProductVariantPrompt();
          }
        }
      }
    };
  }

  /**
   * called by delete button in sidebar
   *
   * @protected
   * @memberof Product
   */
  protected deleteProductPrompt() {
    this.$store.dispatch('openDialog', this.dialogOptionsDeleteProduct);
  }

  /**
   * if delete successfull, router push to /producten/
   *
   * @readonly
   * @type {Options}
   * @memberof Product
   */
  get dialogOptionsDeleteProduct(): Options {
    return {
      title: `Bevestig verwijderen`,
      text: `Weet je zeker dat je dit product wilt verwijderen? Andere varianten van dit product varianten blijven bestaan.`,
      type: 'warning',
      buttons: {
        confirm: {
          text: this.$t('dialogOptions.button.delete').toString(),
          action: () => {
            this.productVariant
              .deleteProduct(this.productVariant.id as string)
              .then((response: any) => this.$router.push('/producten/'))
              .catch((error: AxiosError) => ErrorHandler.network(error));
          }
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
          action: () => {}
        }
      }
    };
  }

  get dialogOptionsDeleteAttribute(): Options {
    return {
      title:
        (this.selectedAttribute as any).title ||
        this.$t('dialogOptions.confirmation').toString(),
      text: this.$tc('dialogOptions.delete', 2, {
        item: this.$tc('attribute.title', 1)
      })
        .toString()
        .toLowerCase(),
      type: 'warning',
      buttons: {
        confirm: {
          text: this.$t('dialogOptions.button.delete').toString(),
          action: () => {
            if (!this.selectedAttribute) {
              return;
            }

            new Attribute(this.selectedAttribute)
              .deleteAttributeValue(this.selectedAttribute.id || '')
              .then((response: any) => {
                if (response) {
                  this.initialize();
                  this.selectedAttribute = null;
                }
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          }
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
          action: () => {
            this.selectedAttribute = null;
          }
        }
      }
    };
  }

  protected checkDefault(image: any) {
    return this.productVariant.image_url === image.url_medium;
  }

  protected get seasonModel(): Season {
    return new Season();
  }

  get tableOptions() {
    return {
      name: {
        singular: 'Variant',
        plural: 'Varianten'
      },
      headers: [
        {
          text: 'Name',
          value: 'name'
        },
        {
          text: 'SKU',
          value: 'sku',
          transform: (sku: string) => {
            return !!sku ? sku : 'Onbekend';
          }
        },
        {
          text: 'Color',
          value: 'color',
          transform: (color: Color) => {
            return color ? color.name : '';
          }
        },
        {
          text: 'Size',
          value: 'size',
          transform: (size: Size) => {
            return size ? size.name : '';
          }
        }
      ],
      actions: [
        {
          type: 'view',
          label: 'view',
          icon: 'remove_red_eye',
          action: '/varianten/{id}',
          tooltip: 'Bekijk variant'
        },
        {
          type: 'delete',
          label: 'delete',
          icon: 'delete',
          action: this.deleteProductVariantPrompt,
          tooltip: 'Verwijder variant'
        }
      ],
      model: new ProductVariant()
      .include([
        ProductVariantIncludes.COLOR,
        ProductVariantIncludes.SIZE,
        ProductVariantIncludes.SUPPLIER_SIZE_OBJECT,
      ]).filter({
        product: this.productVariant.product_uuid,
        exclude: this.productVariant.id
      })
    };
  }

  protected get items(): ActionItem[] {
    return [
      {
        label: 'Edit',
        icon: 'edit',
        action: () => {
          this.RenderFlow.set('isEditingProduct', true);
        },
      },
      {
        label: 'Sync stock',
        icon: 'sync',
        action: () => {
          this.syncStock();
        },
      },
      {
        label: 'Delete variant',
        icon: 'delete',
        action: () => {
          this.deleteProductPrompt();
        },
      },
      {
        label: 'Clone variant',
        icon: 'people',
        action: () => {
          this.isDisplayingQuickAddProductsDialog = true;
        },
      },
      {
        label: 'Copy to variants',
        icon: 'content_copy',
        action: () => {
          this.isDisplayingCopyToVariantDialog = true;
        },
      },
      {
        label: 'CSV Export',
        icon: 'file_download',
        action: () => {
          this.exportProductVariants();
        },
      },
      {
        label: 'CSV Import',
        icon: 'file_upload',
        action: () => {
          this.isUploadingCsv = true;
        },
      },
    ]
  }

  protected getSupplierSizeNameFromSupplierSize(supplierSize: undefined | null | string | SupplierSize ): string {
    if (! supplierSize) { return ''; }
    if (typeof supplierSize === 'string') { return supplierSize; }
    return supplierSize.name || '';
  }

  @Watch('supplierSearch')
  protected supplierSearchChanged(color: string) {
    this.searchSupplierDebounce(color);
  }

  @Watch('colorSearch')
  protected colorSearchChanged(color: string) {
    this.searchColorDebounce(color);
  }

  @Watch('sizeSearch')
  protected sizeSearchChanged(size: string) {
    this.searchSizeDebounce(size);
  }

  @Watch('brandSearch')
  protected brandSearchChanged(brand: string) {
    this.searchBrandDebounce(brand);
  }

  @Watch('RenderFlow.isEditingProduct')
  protected editingChanged(isEditing: boolean) {
    if (isEditing) {
      this.productVariantClone = cloneDeep(this.productVariant);
    }
  }

  @Watch('$route')
  public routeChanged(to: any, from: any) {
    this.initialize();
  }
}

interface Status {
  value?: string;
  text?: string;
}

interface attributeItem {
  title?: string;
  values?: attributeValue[];
}

interface attributeValue {
  attribute?: Attribute,
  option?: any,
}
